import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { cn } from '@utils';
import React from 'react';

import type { FieldGroup } from '@/types/formSchema';

interface RadioCardGroupProps {
  group: FieldGroup;
  formData: Record<string, any>;
  fieldErrors: Record<string, string>;
  disabled?: boolean;
  /** Read-only mode - prevents selection without disabled styling */
  readOnly?: boolean;
  onFieldChange: (fieldId: string, value: string) => void;
  /** Accordion variant wraps in collapsible accordion (used in CreateCase) */
  variant?: 'accordion' | 'inline';
  defaultOpen?: boolean;
  /**
   * Controls the accordion's open state ('accordion' variant only). Omit to leave the
   * component uncontrolled, where `defaultOpen` decides the initial state and the user owns
   * it from there. Pass it when the host needs to re-open the group itself — e.g. to reveal
   * a field a failed submit flagged while the group was collapsed.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ACCORDION_VALUE = 'radio-card-group';

/**
 * Renders selectable card buttons for radio-type fields.
 * Supports two variants:
 * - 'accordion': Wraps cards in a collapsible accordion (for CreateCase)
 * - 'inline': Renders cards directly without wrapper (for CaseInputForm, FieldGroupAccordion)
 */
export function RadioCardGroup({
  group,
  formData,
  fieldErrors,
  disabled = false,
  readOnly = false,
  onFieldChange,
  variant = 'accordion',
  defaultOpen = true,
  open,
  onOpenChange,
}: RadioCardGroupProps) {
  // Honor the schema `hidden` flag here too — this renderer is the metadata/
  // radio path and bypasses the normal isFieldVisible check, so a hidden radio
  // (e.g. a case_type fixed+defaulted per schema) would otherwise still show.
  const radioFields = group.fields.filter(field => field.type === 'radio' && !field.hidden);

  // If every radio in the group is hidden, render nothing rather than an empty
  // accordion/section (e.g. a Metadata group whose only field is a hidden case_type).
  if (radioFields.length === 0) return null;

  // Card content - shared between variants
  const renderCards = () => (
    <div className="space-y-4">
      {radioFields.map(field => (
        <div key={field.id} data-field-id={field.id}>
          {variant === 'inline' && (
            <div className="flex items-center gap-1 mb-3">
              <span className="text-sm font-medium text-foreground">{field.label}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field.options?.map((option, index) => {
              const isSelected = formData[field.id] === option.value;
              const valueAsString = String(option.value);

              return (
                <button
                  key={valueAsString}
                  // The group has no single control, so the first card carries the field's id —
                  // focusing it mirrors how a native radio group takes focus.
                  id={index === 0 ? field.id : undefined}
                  type="button"
                  onClick={() => !disabled && !readOnly && onFieldChange(field.id, valueAsString)}
                  disabled={disabled}
                  className={cn(
                    'flex flex-col p-2 rounded-lg border-2 text-left transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/30',
                    disabled && 'cursor-not-allowed opacity-50',
                    readOnly && 'cursor-default hover:border-border',
                  )}
                >
                  <span className="font-medium text-sm  text-foreground">{option.label}</span>
                  {option.description && (
                    <span className="text-sm text-muted-foreground">{option.description}</span>
                  )}
                </button>
              );
            })}
          </div>
          {fieldErrors[field.id] && (
            <p className="mt-2 text-sm text-destructive">{fieldErrors[field.id]}</p>
          )}
        </div>
      ))}
    </div>
  );

  // Inline variant - no accordion wrapper
  if (variant === 'inline') {
    return <div className="rounded-lg border border-border p-4 mb-4 bg-card">{renderCards()}</div>;
  }

  // Accordion variant - with collapsible wrapper
  return (
    <Accordion
      type="single"
      collapsible
      {...(open === undefined
        ? { defaultValue: defaultOpen ? ACCORDION_VALUE : undefined }
        : {
            value: open ? ACCORDION_VALUE : '',
            onValueChange: (value: string) => onOpenChange?.(value === ACCORDION_VALUE),
          })}
      className="bg-card rounded-xl border"
    >
      <AccordionItem value={ACCORDION_VALUE} className="border-none" disabled={disabled}>
        <AccordionTrigger className="px-6 py-4 text-lg font-medium hover:bg-muted rounded-lg data-[state=open]:rounded-b-none">
          {radioFields.map(field => (
            <span key={field.id} className="flex items-center gap-1">
              {field.label}
              {field.validation?.required && <span className="text-destructive">*</span>}
            </span>
          ))}
        </AccordionTrigger>
        <AccordionContent className="px-6 pb-6">
          <div className="mt-4">{renderCards()}</div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
